/**
 * Pan Calculations
 *
 * Functions for calculating stereo panning based on listener/source positions
 *
 * 360° PANNING SPECIFICATION:
 * ┌──────────────┬─────────────┬────────────────────────────────┐
 * │ Angle        │ Pan (L, R)  │ Description                    │
 * ├──────────────┼─────────────┼────────────────────────────────┤
 * │ 0° (front)   │ L100, R100  │ Sound in both ears (center)    │
 * │ 45° (f-right)│ L50, R100   │ Mostly right, some left        │
 * │ 90° (right)  │ L0, R100    │ Full RIGHT ear only            │
 * │ 135° (b-right)│ L50, R100  │ Mostly right, some left        │
 * │ 180° (behind)│ L100, R100  │ Sound in both ears (center)    │
 * │ 225° (b-left)│ L100, R50   │ Mostly left, some right        │
 * │ 270° (left)  │ L100, R0    │ Full LEFT ear only             │
 * │ 315° (f-left)│ L100, R50   │ Mostly left, some right        │
 * └──────────────┴─────────────┴────────────────────────────────┘
 */

import {Position, ListenerRight} from "../../types/position";

/**
 * Panning values for left/right channels
 */
export interface Panning {
  left: number; // 0-100 percentage
  right: number; // 0-100 percentage
}

/**
 * Calculate listener's right-ear vector from yaw rotation
 *
 * CLOCKWISE ROTATION (Game Engine Standard):
 *   0°   → Facing +Z (forward), right ear → +X
 *   90°  → Facing +X (right),   right ear → -Z
 *   180° → Facing -Z (back),    right ear → -X
 *   270° → Facing -X (left),    right ear → +Z
 *
 * @param yawDegrees Yaw rotation in degrees (CLOCKWISE from +Z)
 * @returns Unit vector pointing to listener's right ear (X, Z components)
 */
export function calculateListenerRight(yawDegrees: number): ListenerRight {
  // CLOCKWISE rotation formula (standard game engine convention)
  const yawRad = (yawDegrees * Math.PI) / 180;

  // Right ear vector:
  // cos(yaw) for X component
  // -sin(yaw) for Z component (NEGATIVE sine for CLOCKWISE)
  return {
    x: Math.cos(yawRad),
    z: -Math.sin(yawRad),
  };
}

/**
 * Calculate pan value from listener and source positions
 *
 * Uses atan2 + sin for TRUE 360° spatial audio:
 * - 0° (front): pan = 0 → center
 * - 90° (right): pan = +1 → full right
 * - 180° (behind): pan = 0 → center
 * - 270° (left): pan = -1 → full left
 *
 * @param listenerPos Listener position
 * @param sourcePos Sound source position
 * @param listenerRight Listener's right-ear unit vector
 * @returns Pan value from -1 (full left) to +1 (full right)
 */
export function calculatePanFromPositions(
  listenerPos: Position,
  sourcePos: Position,
  listenerRight: ListenerRight
): number {
  // Calculate relative vector (speaker relative to listener)
  const vecToSource = {
    x: sourcePos.x - listenerPos.x,
    z: sourcePos.z - listenerPos.z,
  };

  // Project onto listener's right-ear axis using dot product
  // Positive = sound is to the RIGHT of listener
  // Negative = sound is to the LEFT of listener
  const dxLocal =
    vecToSource.x * listenerRight.x + vecToSource.z * listenerRight.z;

  // Calculate forward component (dzLocal)
  // Forward vector is right vector rotated 90° counter-clockwise: (x,z) -> (-z,x)
  const listenerForward = {x: -listenerRight.z, z: listenerRight.x};
  const dzLocal =
    vecToSource.x * listenerForward.x + vecToSource.z * listenerForward.z;

  // TRUE 360° SPATIAL AUDIO PANNING
  // Calculate angle from listener to source using atan2
  // atan2(dxLocal, dzLocal) gives angle from forward direction
  // Range: -π to +π radians (-180° to +180°)
  const angleToSource = Math.atan2(dxLocal, dzLocal);

  // Convert to pan value using sine
  // sin(angle) maps:
  //   0° (front)  → sin(0) = 0      → CENTER
  //   45° (f-R)   → sin(π/4) ≈ 0.71 → PARTIAL RIGHT
  //   90° (right) → sin(π/2) = 1    → FULL RIGHT
  //   135° (b-R)  → sin(3π/4) ≈ 0.71→ PARTIAL RIGHT
  //   180° (back) → sin(π) = 0      → CENTER
  //   -45° (f-L)  → sin(-π/4) ≈-0.71→ PARTIAL LEFT
  //   -90° (left) → sin(-π/2) = -1  → FULL LEFT
  //   -135° (b-L) → sin(-3π/4)≈-0.71→ PARTIAL LEFT
  const rawPanValue = Math.sin(angleToSource);

  return rawPanValue;
}

/**
 * Convert pan value (-1 to +1) to left/right channel percentages
 *
 * EXACT SPECIFICATION:
 * ┌───────────┬──────────┬───────────┬─────────────────────┐
 * │ Pan Value │ Left Ear │ Right Ear │ Angle (from front)  │
 * ├───────────┼──────────┼───────────┼─────────────────────┤
 * │ -1.0      │ 100%     │ 0%        │ 270° (full left)    │
 * │ -0.71     │ 100%     │ 50%       │ 315° or 225°        │
 * │ -0.5      │ 100%     │ 50%       │ ~330° or ~210°      │
 * │ 0.0       │ 100%     │ 100%      │ 0° or 180° (center) │
 * │ +0.5      │ 50%      │ 100%      │ ~30° or ~150°       │
 * │ +0.71     │ 50%      │ 100%      │ 45° or 135°         │
 * │ +1.0      │ 0%       │ 100%      │ 90° (full right)    │
 * └───────────┴──────────┴───────────┴─────────────────────┘
 *
 * @param pan Pan value from -1 (full left) to +1 (full right)
 * @returns Panning percentages for left and right channels
 */
export function panValueToPanning(pan: number): Panning {
  const clamped = Math.max(-1, Math.min(1, pan));

  // Convert pan (-1 to +1) to left/right percentages
  // pan = 0 (center): L=100%, R=100%
  // pan = +1 (right): L=0%, R=100%
  // pan = -1 (left): L=100%, R=0%
  //
  // Formula:
  // - Left ear reduces as pan goes positive (right)
  // - Right ear reduces as pan goes negative (left)

  let left: number;
  let right: number;

  if (clamped >= 0) {
    // Sound is to the RIGHT (or center)
    // Right ear stays at 100%
    // Left ear reduces from 100% to 0% as pan goes 0 → +1
    left = 100 * (1 - clamped);
    right = 100;
  } else {
    // Sound is to the LEFT
    // Left ear stays at 100%
    // Right ear reduces from 100% to 0% as pan goes 0 → -1
    left = 100;
    right = 100 * (1 + clamped); // clamped is negative, so this reduces
  }

  return {
    left: Math.round(Math.max(0, Math.min(100, left))),
    right: Math.round(Math.max(0, Math.min(100, right))),
  };
}
